import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { resolveImageSrc, resolveVideoSrc } from '../utils/cloudinary';

const statusStyles = {
  shipped: 'border-cream-300 bg-cream-300 text-inkwell-800',
  progress: 'border-amber-200 bg-amber-50 text-amber-800',
  handed: 'border-cream-300 bg-cream-50 text-inkwell-600',
};

function ProjectPlaceholder({ project }) {
  // Generate a unique placeholder based on the project
  const { title, color } = project;
  const isDark = ['#2b3a8a', '#1a1450'].includes(color);

  // Different visual styles per project number
  if (project.no === '01') {
    return (
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: color }}>
        <div className="relative w-full h-full">
          <div className="absolute top-6 right-8 px-3 py-1.5 rounded-md bg-white shadow-md text-[11px]">perdido</div>
          <div className="absolute bottom-10 left-8 px-3 py-1.5 rounded-md bg-white shadow-md text-[11px]">miedo</div>
          <div className="absolute top-1/2 left-6 -translate-y-1/2 px-2.5 py-1 rounded bg-white shadow-md text-[10px]">la cima</div>
          <div className="absolute top-1/3 right-10 px-2 py-1 rounded bg-white shadow-md text-[10px]">una montaña</div>
          <div className="absolute bottom-6 right-12 text-[9px] px-2 py-1 bg-white/90 rounded">A1 🌱</div>
        </div>
      </div>
    );
  }
  if (project.no === '02') {
    // Splunk - AI investigation dark card
    return (
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: 'linear-gradient(135deg, #fde2e4 0%, #f5cdcf 100%)' }}>
        <div className="bg-[#0a0a0a] rounded-lg p-4 w-[80%] shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-1.5">
              <span className="text-[#e8a5c8]">✨</span>
              <span className="text-white text-[11px]">AI investigation</span>
            </div>
            <span className="text-blue-400 text-[10px]">View</span>
          </div>
          <p className="text-white/80 text-[9.5px] leading-snug">
            On 4 Aug, 2025 at 1:00 PM, fewer than 75% of <span className="bg-white/10 px-1 rounded">pan_globalprotect</span> events ingested contained CIM-compliant field <span className="bg-white/10 px-1 rounded">src</span>.
          </p>
        </div>
      </div>
    );
  }
  if (project.no === '03') {
    // GCal Wrapped - dark navy with calendar icon and bubbles
    return (
      <div className="absolute inset-0 flex items-center justify-center" style={{ background: '#1a2a5e' }}>
        <div className="relative">
          {/* Stylized GCal icon */}
          <div className="w-20 h-20 grid grid-cols-2 gap-0.5 rounded-lg overflow-hidden">
            <div className="bg-[#4285f4]" />
            <div className="bg-[#ea4335]" />
            <div className="bg-[#fbbc04]" />
            <div className="bg-[#34a853]" />
          </div>
          <div className="absolute -top-3 -right-12 bg-white text-[9px] px-2 py-1 rounded shadow">I have comments!</div>
          <div className="absolute -bottom-3 -left-10 bg-white text-[9px] px-2 py-1 rounded shadow">Work Today</div>
          <div className="absolute -top-5 left-1/2 text-[#f9d56e] text-xl">✨</div>
        </div>
        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 text-white text-2xl tracking-tight">GCal Wrapped</div>
      </div>
    );
  }
  if (project.no === '04') {
    // Sklňuj - declension cases
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center" style={{ background: color }}>
        <div className=" text-3xl tracking-widest text-inkwell-900">SKLOŇUJ</div>
        <div className="text-[10px] text-inkwell-500 mt-1">Czech declension practice</div>
        <div className="grid grid-cols-3 gap-1.5 mt-3">
          {['Nominative', 'Genitive', 'Dative', 'Accusative', 'Vocative', 'Locative'].map((c, i) => (
            <div key={c} className="px-2 py-1 rounded-full bg-white text-[9px] flex items-center gap-1 shadow-sm">
              <span className="w-3 h-3 rounded-full flex items-center justify-center text-white text-[8px]" style={{ background: ['#a85cf5','#f59e0b','#10b981','#3b82f6','#a855f7','#ef4444'][i] }}>{i+1}</span>
              {c}
            </div>
          ))}
        </div>
      </div>
    );
  }
  if (project.no === '05') {
    // Loop - cloud/sky with envelope
    return (
      <div className="absolute inset-0" style={{ background: 'linear-gradient(to bottom, #87ceeb 0%, #b8def0 100%)' }}>
        {/* Clouds */}
        <div className="absolute top-4 left-6 w-12 h-3 bg-white rounded-full opacity-80" />
        <div className="absolute top-10 right-8 w-16 h-4 bg-white rounded-full opacity-90" />
        <div className="absolute bottom-12 left-10 w-10 h-3 bg-white rounded-full opacity-70" />
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="bg-white rounded-md px-3 py-2 shadow-lg text-center">
            <div className="text-orange-500 text-base leading-tight">Less inbox,</div>
            <div className="text-orange-500 text-base leading-tight">more Cornell</div>
          </div>
        </div>
        <div className="absolute bottom-3 right-4 w-8 h-6 bg-blue-500 rounded" />
      </div>
    );
  }
  if (project.no === '06') {
    // Copilot for Sales - form mockup
    return (
      <div className="absolute inset-0 p-3" style={{ background: color }}>
        <div className="bg-white rounded-md p-2.5 h-full text-[9px] shadow-sm">
          <div className="text-inkwell-700 font-semibold mb-1">Product needs</div>
          <div className="text-inkwell-500 text-[8px] mb-1.5">Tell us what you're looking for...</div>
          <div className="border border-cream-300 rounded p-1.5 text-[8.5px] text-inkwell-700">I need to buy 100 office printers for my company by the 14th of next month.</div>
        </div>
      </div>
    );
  }
  // CU Reviews - rating dashboard
  if (project.no === '07') {
    return (
      <div className="absolute inset-0 p-4" style={{ background: color }}>
        <div className="bg-white rounded-md p-2 shadow text-[9px] flex items-center gap-3">
          <div>
            <div className="text-2xl font-bold text-inkwell-900">4.6</div>
            <div className="text-[7px] text-inkwell-500">Overall Satisfaction</div>
            <div className="text-orange-400 text-[10px]">★★★★½</div>
          </div>
          <div className="flex-1 space-y-1">
            <div className="flex items-center gap-1 text-[8px]">Difficulty <span className="flex-1 h-1 bg-red-200 rounded"><span className="block h-full w-3/4 bg-red-400 rounded" /></span><span>4.0</span></div>
            <div className="flex items-center gap-1 text-[8px]">Workload <span className="flex-1 h-1 bg-yellow-200 rounded"><span className="block h-full w-1/2 bg-yellow-400 rounded" /></span><span>3.0</span></div>
          </div>
        </div>
        <div className="absolute bottom-3 left-4 px-2 py-1 bg-white rounded shadow text-[8px]">Should I take CS 4820?</div>
        <div className="absolute top-3 right-3 w-6 h-6 rounded-full bg-orange-400" />
      </div>
    );
  }
  return (
    <div className="absolute inset-0 flex items-center justify-center text-2xl" style={{ background: color, color: isDark ? '#fff' : '#222' }}>
      {title}
    </div>
  );
}

function ProjectMedia({ project }) {
  const cardVideoSize = { width: 800, height: 450 };
  const videoRef = useRef(null);
  const [isTouch, setIsTouch] = useState(false);

  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(hover: none), (pointer: coarse)');
    const update = () => setIsTouch(mq.matches);
    update();
    mq.addEventListener?.('change', update);
    return () => mq.removeEventListener?.('change', update);
  }, []);

  // On touch devices we keep the current autoplay behavior; the autoPlay
  // attribute only fires at load, so kick playback off once we know it's touch.
  useEffect(() => {
    if (!isTouch) return;
    const v = videoRef.current;
    if (!v) return;
    const p = v.play();
    if (p && typeof p.catch === 'function') p.catch(() => {});
  }, [isTouch]);

  if (project.video) {
    const videoSrc = resolveVideoSrc(project.video, cardVideoSize);

    const handleEnter = () => {
      if (isTouch) return;
      const v = videoRef.current;
      if (!v) return;
      const p = v.play();
      if (p && typeof p.catch === 'function') p.catch(() => {});
    };

    const handleLeave = () => {
      if (isTouch) return;
      videoRef.current?.pause();
    };

    return (
      <video
        ref={videoRef}
        className={`absolute inset-0 w-full h-full ${project.id !== 4 ? "object-cover" : "object-contain"}`}
        loop
        muted
        playsInline
        preload="metadata"
        onMouseEnter={handleEnter}
        onMouseLeave={handleLeave}
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        onContextMenu={(e) => e.preventDefault()}
        style={{ backgroundColor: project.color }}
      >
        <source src={videoSrc} type="video/mp4" />
      </video>
    );
  }

  if (project.thumbnail) {
    return (
      <img
        src={resolveImageSrc(project.thumbnail, { width: 800 })}
        alt={project.title}
        className="absolute inset-0 w-full h-full object-cover"
        loading="lazy"
      />
    );
  }

  return <ProjectPlaceholder project={project} />;
}

export default function ProjectCard({ project }) {
  return (
    <Link to={`/work/${project.slug}`} className="block">
      <article data-platform="true" className="project-card relative bg-cream-50 rounded-lg p-4 border border-cream-300/50">
        <div className="flex items-center justify-between mb-3">
          <div className="card-pin project-pin" />
          <span className=" text-[10px] uppercase tracking-wider text-inkwell-400">No. {project.no}</span>
        </div>

        <div
          className={`relative w-full rounded-md overflow-hidden mb-4 ${
            project.video ? 'max-w-[800px] mx-auto aspect-[16/9]' : 'aspect-[16/9]'
          }`}
        >
          <div className="project-media absolute inset-0">
            <ProjectMedia project={project} />
          </div>
        </div>

        <div className="flex items-start justify-between gap-3">
          <h3 className=" text-[20px] leading-tight text-inkwell-900">{project.title}</h3>
          <div className="flex gap-1.5 flex-shrink-0 flex-wrap justify-end">
            {project.tags.map((t) => (
              <span
                key={t}
                className={`px-2 py-0.5 rounded border text-[9px] uppercase tracking-wider ${statusStyles[project.statusColor] || 'border-cream-300 bg-cream-50 text-inkwell-600'}`}
              >
                {t}
              </span>
            ))}
          </div>
        </div>
        <p className="mt-2.5 text-[12.5px] text-inkwell-700 leading-relaxed">{project.description}</p>

        <div className="project-card-details">
          <div className="project-card-details-inner">
            <dl className="space-y-2.5 pt-3">
              <div className="flex items-baseline justify-between gap-6">
                <dt className=" text-[11px] uppercase tracking-wider text-inkwell-400 shrink-0">Role</dt>
                <dd className="text-[12px] text-inkwell-900 leading-snug text-right">{project.role}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-6">
                <dt className=" text-[11px] uppercase tracking-wider text-inkwell-400 shrink-0">Team</dt>
                <dd className="text-[12px] text-inkwell-900 leading-snug text-right">{project.team}</dd>
              </div>
              <div className="flex items-baseline justify-between gap-6">
                <dt className=" text-[11px] uppercase tracking-wider text-inkwell-400 shrink-0">Time frame</dt>
                <dd className="text-[12px] text-inkwell-900 leading-snug text-right">{project.timeframe}</dd>
              </div>
            </dl>
          </div>
        </div>
      </article>
    </Link>
  );
}
